import axios from 'axios';
import { PaginatedResponse } from '../types';
import {
  BUILDINGS_LIST_PAGE_SIZE,
  BUILDINGS_SELECTOR_FETCH_LIMIT,
  BuildingsListResponse,
  isPaginatedBuildingsResponse,
  unwrapBuildingsList,
} from './buildingsList.util';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

export interface Operator {
  id: string;
  name: string;
  email: string;
  username: string;
}

export interface Building {
  id: string;
  name: string;
  projectCode?: string;
  managementPhone?: string;
  managementEmail?: string;
  logoPath?: string;
  address?: string;
  isActive: boolean;
  allowOnlinePayment: boolean;
  allowPartialPayment: boolean;
  combinedElevatorVehicleCards?: boolean;
  autoSendEmailReminder: boolean;
  serviceFeeDay: number;
  paymentFromDay: number;
  paymentToDay: number;
  apartmentCount: number;
  parkingLegacyApiKey?: string | null;
  operatorId: string;
  createdAt: string;
  updatedAt: string;
  operator?: {
    id: string;
    name: string;
    email: string;
  };
  _count?: {
    apartments: number;
    users: number;
  };
}

export interface CreateBuildingDto {
  name: string;
  projectCode?: string;
  managementPhone?: string;
  managementEmail?: string;
  logoPath?: string;
  address?: string;
  isActive?: boolean;
  allowOnlinePayment?: boolean;
  allowPartialPayment?: boolean;
  combinedElevatorVehicleCards?: boolean;
  autoSendEmailReminder?: boolean;
  serviceFeeDay?: number;
  paymentFromDay?: number;
  paymentToDay?: number;
  operatorId?: string; // For SUPERADMIN to specify operator
  parkingLegacyApiKey?: string | null;
}

export interface UpdateBuildingDto extends Partial<CreateBuildingDto> {
  parkingLegacyApiKey?: string | null;
}

class BuildingsApi {
  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  async getOperators(): Promise<Operator[]> {
    const response = await axios.get(`${API_BASE_URL}/buildings/operators`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async listBuildings(params?: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<PaginatedResponse<Building>> {
    const response = await axios.get<BuildingsListResponse>(`${API_BASE_URL}/buildings`, {
      headers: this.getAuthHeaders(),
      params: {
        page: params?.page ?? 1,
        limit: params?.limit ?? BUILDINGS_LIST_PAGE_SIZE,
        search: params?.search?.trim() || undefined,
      },
    });
    const payload = response.data as BuildingsListResponse;
    if (isPaginatedBuildingsResponse(payload)) {
      return {
        data: payload.data as Building[],
        meta: payload.meta,
      };
    }
    const list = unwrapBuildingsList(payload) as Building[];
    return {
      data: list,
      meta: {
        total: list.length,
        page: 1,
        limit: list.length || BUILDINGS_LIST_PAGE_SIZE,
        totalPages: 1,
      },
    };
  }

  /** All buildings visible to the user (for dropdowns). Fetches paginated API in batches. */
  async getBuildings(): Promise<Building[]> {
    const firstPage = await this.listBuildings({
      page: 1,
      limit: BUILDINGS_SELECTOR_FETCH_LIMIT,
    });
    const buildings = [...firstPage.data];
    const totalPages = firstPage.meta.totalPages;
    for (let page = 2; page <= totalPages; page += 1) {
      const nextPage = await this.listBuildings({
        page,
        limit: BUILDINGS_SELECTOR_FETCH_LIMIT,
      });
      buildings.push(...nextPage.data);
    }
    return buildings;
  }

  async getBuilding(id: string): Promise<Building> {
    const response = await axios.get(`${API_BASE_URL}/buildings/${id}`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async createBuilding(building: CreateBuildingDto): Promise<Building> {
    const response = await axios.post(`${API_BASE_URL}/buildings`, building, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async updateBuilding(id: string, building: UpdateBuildingDto): Promise<Building> {
    const response = await axios.patch(`${API_BASE_URL}/buildings/${id}`, building, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async deleteBuilding(id: string): Promise<void> {
    await axios.delete(`${API_BASE_URL}/buildings/${id}`, {
      headers: this.getAuthHeaders(),
    });
  }

  async uploadBuildingLogoFile(file: File, operatorId?: string): Promise<{ logoPath: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('token');
    const response = await axios.post(`${API_BASE_URL}/buildings/logo/upload`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
      params: operatorId ? { operatorId } : undefined,
    });
    return response.data;
  }

  async uploadBuildingLogo(id: string, file: File): Promise<Building> {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('token');
    const response = await axios.post(`${API_BASE_URL}/buildings/${id}/logo/upload`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async removeBuildingLogo(id: string): Promise<Building> {
    const token = localStorage.getItem('token');
    const response = await axios.delete(`${API_BASE_URL}/buildings/${id}/logo`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }
}

export const buildingsApi = new BuildingsApi(); 